/**
 * Footer 5014 — KUK footer.
 *
 * A solid band with optional link columns and a centred copyright bar. Ported
 * from the landing-page branch of kuk-campus-portal's
 * `components/organisms/Footer.tsx`, where the columns, social icons and
 * disclaimer are suppressed by sniffing the URL; here each is a CMS toggle.
 *
 * Link columns live in `footer_item` rows under `footer_category`, so this uses
 * Form_3's nested editor. The category and item field configs are shared with
 * Footer 1034 rather than duplicated — the child tables are identical.
 *
 * Every field maps to a real `footer` column. Two were added for this variant:
 * `privacy_link_*` (20260901130000) and `heading_underline_color`
 * (20260908120000); the rest already existed.
 */

import { FOOTER_1034_CONSTANTS } from "./footer_1034";


export const FOOTER_5014_CONSTANTS = {
  FOOTER_FIELDS_CONFIG: [
    {
      fieldName: "background_color",
      label: "Band background",
      value: "#031F44",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#031F44",
      required: false,
      defaultValue: "#031F44",
    },
    {
      fieldName: "gradient_start_color",
      label: "Band gradient — start",
      value: "",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Optional",
      description: "Set BOTH stops for a gradient; leave either empty to use the flat colour above.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "gradient_end_color",
      label: "Band gradient — end",
      value: "",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Optional",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "enable_footer_content",
      label: "Show the link columns",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Off on a landing page, on for a full site footer",
      required: false,
      defaultValue: true,
    },
    {
      fieldName: "title",
      label: "Disclaimer line",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Optional line above the columns",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "title_text_color",
      label: "Disclaimer colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "text_color",
      label: "Link colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      description: "Default for column headings and links; each can override it.",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "heading_underline_color",
      label: "Heading rule colour",
      value: "#00aded",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#00aded",
      description:
        "Accent bar under each column heading, drawn at 40% so the band shows through. Clear it for no rule.",
      required: false,
      defaultValue: "#00aded",
    },
    {
      fieldName: "column_count",
      label: "Columns (768px and up)",
      value: 3,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "3",
      description: "1 to 4. Columns always stack below 768px.",
      required: false,
      defaultValue: 3,
    },
    {
      fieldName: "enable_copyright",
      label: "Show the copyright bar",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      required: false,
      defaultValue: true,
    },
    {
      fieldName: "copyright_text",
      label: "Copyright text",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. © Copyright 2026 of Kurukshetra University | All Rights Reserved",
      description: "Plain text. Add a Privacy Policy link as a one-item link column.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "copyright_text_color",
      label: "Copyright colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "privacy_link_label",
      label: "Policy link text",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Privacy Policy",
      description:
        "Shown inline at the end of the copyright sentence. Leave empty for no link.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "privacy_link_url",
      label: "Policy link URL",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "/privacy-policy",
      description: "Where the policy link points.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "status",
      label: "Status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Enable/Disable section",
      required: false,
      defaultValue: true,
    },
  ],

  /** Shared with Footer 1034 — same `footer_category` / `footer_item` tables. */
  CATEGORY_FIELDS_CONFIG: FOOTER_1034_CONSTANTS.CATEGORY_FIELDS_CONFIG,
  FOOTER_TABLE_FIELDS_CONFIG: FOOTER_1034_CONSTANTS.FOOTER_TABLE_FIELDS_CONFIG,
  DEFAULT_ROW: FOOTER_1034_CONSTANTS.DEFAULT_ROW,
};
